import { useState, useEffect, useRef, useMemo } from "react";
import { DialogContent, Box, Grid, Typography } from "@mui/material";
import { Inventory2Outlined } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { Util } from "../../util/Util";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TableView from "../../components/TableView";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";

import { createSignCell } from "../../util/cellFormatters";


import { roundMoney, roundQty } from "../../util/quantity";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
}

const EXPORTABLE_COLUMN_IDS = new Set([
	"sector",
	"category",
	"item_code",
	"item_name",
	"quantity",
	"snap_shot",
	"stock_difference",
	"amount",
	"total",
	"snap_total",
	"value_difference",
]);

const InventoryReport = ({ isOpen, toggle, bundle }: Props) => {
	const { api } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const themeColor = useMoleThemeColor();
	const [record, setRecord] = useState<any>(null);
	const tableRef = useRef<MRT_TableInstance<any> | null>(null);

	const getVisibleExportColumns = () => {
		const table = tableRef.current;
		if (table) {
			return table
				.getVisibleLeafColumns()
				.filter((column) => EXPORTABLE_COLUMN_IDS.has(column.id))
				.map((column) => ({
					id: column.id,
					header: String(column.columnDef.header ?? column.id),
				}));
		}

		return columns
			.filter((column) => EXPORTABLE_COLUMN_IDS.has(column.id || String(column.accessorKey ?? "")))
			.map((column) => ({
				id: column.id || String(column.accessorKey ?? ""),
				header: String(column.header ?? column.id ?? column.accessorKey ?? ""),
			}));
	};

	const handleExport = async () => {
		if (!bundle?.batch) return;

		const exportColumns = getVisibleExportColumns();
		if (exportColumns.length === 0) {
			toast.warn(t("common:toast.warn.noColumnsVisible"));
			return;
		}

		setLoadingMessage("EXPORT");
		const res = await api.post(`reports/inventories/${bundle.batch}`, {
			columns: exportColumns,
		});
		if (res) {
			if (res.status === 200 && res.data?.data) {
				const pdfData = atob(res.data.data);
				const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], {
					type: "application/pdf",
				});
				window.open(URL.createObjectURL(blob));
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(() => handleExport());
				return;
			} else {
				console.error("Failed to export inventory report:", res);
			}
		}
		setLoadingMessage(null);
	};

	const fetchData = async () => {
		setLoadingMessage("FETCH");

		const res = await api.get(`inventories/${bundle.batch}`);

		if (res) {
			if (res.status === 200) {
				setRecord(res.data);
				setLoadingMessage(null);
				return;
			} else if (res.status === 404) {
				toast.warn(t("common:toast.warn.noRecord"));
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
				return;
			} else {
				console.error("Failed to fetch data:", res);
			}
		}
		setLoadingMessage(null);
		toggle();
	};


	useEffect(() => {
		if (isOpen && bundle) {
			fetchData();
		}
	}, [isOpen]);


	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			id: "sector",
			header: t("common:tables.sector"),
			accessorKey: "sector",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			id: "category",
			header: t("common:tables.category"),
			accessorKey: "category",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			id: "item_code",
			header: t("common:tables.itemCode"),
			accessorKey: "item_code",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			id: "item_name",
			header: t("common:tables.item"),
			accessorKey: "item_name",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			id: "quantity",
			header: t("common:tables.count"),
			accessorFn: (row: any) => Number(row.quantity),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			id: "snap_shot",
			header: t("common:tables.stock"),
			accessorFn: (row: any) => Number(row.snap_shot),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			id: "stock_difference",
			header: t("common:tables.stockDifference"),
			accessorFn: (row: any) => Number(row.quantity) - Number(row.snap_shot),
			Cell: createSignCell(),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			id: "amount",
			header: t("common:tables.unitValue"),
			accessorFn: (row: any) => Number(row.amount),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			id: "total",
			header: t("common:tables.countValue"),
			accessorFn: (row: any) => Number(row.total),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.total);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);


				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			id: "snap_total",
			header: t("common:tables.stockValue"),
			accessorFn: (row: any) => Number(row.snap_total),
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.snap_total);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);


				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			id: "value_difference",
			header: t("common:tables.valueDifference"),
			accessorFn: (row: any) => roundMoney(Number(row.total) - Number(row.snap_total)),
			Cell: createSignCell(),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = roundMoney(Number(rows[i].original.total) - Number(rows[i].original.snap_total));
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);


				return (
					<Box sx={{ fontWeight: 'bold', color: total >= 0 ? 'success.main' : 'error.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		}
	], [t]);



	return (
		<>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxWidth: "95%", width: "95%", maxHeight: "95vh" }}
			>
				<MoleDialogHeader
					title={t("common:inventory.headers.report")}
					subtitle={`${record?.details.depot || ""} · ${record?.details.reference || ""}`}
					icon={<Inventory2Outlined />}
					onClose={toggle}
					themeColor={themeColor}
				/>
				<DialogContent>
					<Box >
						<Grid container spacing={2} sx={{ mb: 2 }}>
							{/* Helper to avoid repeating the same markup */}
							{([
								{ label: t("common:tables.date"), value: Util.formatDate(record?.details.date, 'full') },
								{ label: t("common:tables.observer"), value: record?.details.client },
								{ label: t("common:tables.user"), value: record?.details.user },
								{ label: t("common:report.notes"), value: record?.details.description },
							] as const).map(({ label, value }) => (
								<Grid size="grow" key={label}>
									<Typography variant="subtitle2" color="text.secondary" gutterBottom>
										{label}
									</Typography>
									<Typography variant="body1" fontWeight={500}>
										{value ?? t("common:status.noDetails")}
									</Typography>
								</Grid>
							))}
						</Grid>
					</Box>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<TableView
							data={record?.records || []}
							columns={columns}
							includeTimestampColumns={false}
							totals={[
								{ key: "total", label: t("common:tables.countValue") },
								{ key: "snap_total", label: t("common:tables.stockValue") },
								{
									accessorFn: (row) => roundMoney(Number(row.total) - Number(row.snap_total)),
									label: t("common:tables.valueDifference"),
								},
							]}
							bundle={bundle}
							enableRowActions={false}

							renderTopToolbarActions={(table) => {
								tableRef.current = table;
								return (
								<Box sx={{ display: 'flex', gap: '0.5rem' }}>
									<ExportMenuButton
										prefix="inventory_report"
										data={record?.records || []}
										columns={columns}
										table={table}
									/>
								</Box>
								);
							}}
						/>

					</LocalizationProvider>
				</DialogContent>
				<MoleDialogActions>
					<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.close")}</MoleCancelButton>
					<MolePrimaryButton
						startIcon={<FontAwesomeIcon icon={faFilePdf} />}
						onClick={handleExport}
						disabled={!record}
					>
						{t("common:ui.buttons.export")}
					</MolePrimaryButton>
				</MoleDialogActions>
			</MoleDialog>

		</>
	);
};

export default InventoryReport;
